import { useDeleteUserMutation, userQuery } from "@common/api/users"
import { ConfirmDialog } from "@common/components/shared/confirm-dialog"
import { useReAuthGuard } from "@common/hooks/use-reauth-guard"
import { handleError } from "@common/utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import useCustomToast from "@/hooks/use-custom-toast"
import { displayName } from "../../-lib/display-name"

export const Route = createFileRoute(
  "/_layout/settings/_panel/workspace/members/_list/$userId/delete",
)({
  component: DeleteMemberDialog,
})

function DeleteMemberDialog(): React.ReactElement {
  const { userId } = Route.useParams()
  const { data: user } = useSuspenseQuery(userQuery({ user_id: userId }))
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const deleteMutation = useDeleteUserMutation()
  const { guard, handleFreshnessError, reAuthModal } = useReAuthGuard()
  const name = displayName(user)

  const close = () =>
    navigate({
      to: "/settings/workspace/members/$userId",
      params: { userId },
      search: true,
    })

  const confirm = () => {
    const send = () =>
      deleteMutation.mutate(
        { user_id: userId },
        {
          onSuccess: () => {
            showToast("Archived", `${name} archived.`, "success")
            navigate({ to: "/settings/workspace/members", search: true })
          },
          onError: (error) => {
            if (handleFreshnessError(error, send)) return
            handleError(error, showToast)
          },
        },
      )
    guard(send)
  }

  return (
    <>
      <ConfirmDialog
        title={`Archive ${name}?`}
        description="They will no longer be able to sign in to your workspace."
        action="Archive"
        tone="default"
        pendingAction="Archiving…"
        pending={deleteMutation.isPending}
        onConfirm={confirm}
        onCancel={close}
      />
      {reAuthModal}
    </>
  )
}
